import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, Field, Input, Spinner, useToast } from "@aigntx/ui";
import { endpoints } from "../api";
import { useAuth } from "../auth";
import { useTenantPatch } from "../tenant";
import type { TenantConfig } from "../types";
import { TeamManager } from "./UsersPage";
import { BrandingEditor } from "./BrandingPage";

type Tab = "profile" | "team" | "branding";

export function CompanyPage() {
  const { t } = useTranslation();
  const { can } = useAuth();

  const tabs = useMemo(() => {
    const list: { key: Tab; label: string }[] = [];
    if (can("tenant.read")) list.push({ key: "profile", label: t("company.profile") });
    if (can("users.read")) list.push({ key: "team", label: t("company.team") });
    if (can("tenant.manage")) list.push({ key: "branding", label: t("company.branding") });
    return list;
  }, [can, t]);

  const [tab, setTab] = useState<Tab>(tabs[0]?.key ?? "profile");

  return (
    <div className="page">
      <header className="page__head">
        <h1 className="page__title">{t("company.title")}</h1>
        <p className="page__sub">{t("company.subtitle")}</p>
      </header>

      <div className="tabs" role="tablist">
        {tabs.map((tb) => (
          <button
            key={tb.key}
            role="tab"
            aria-selected={tab === tb.key}
            className={tab === tb.key ? "tab is-on" : "tab"}
            onClick={() => setTab(tb.key)}
          >
            {tb.label}
          </button>
        ))}
      </div>

      <div className="tab-body">
        {tab === "profile" && <CompanyProfileForm />}
        {tab === "team" && <TeamManager />}
        {tab === "branding" && <BrandingEditor />}
      </div>
    </div>
  );
}

const PROFILE_FIELDS = [
  "legalName",
  "industry",
  "phone",
  "email",
  "website",
  "address",
] as const;
type ProfileField = (typeof PROFILE_FIELDS)[number];

function CompanyProfileForm() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const toast = useToast();
  const patchTenant = useTenantPatch();
  const canManage = can("tenant.manage");
  const [config, setConfig] = useState<TenantConfig | null>(null);
  const [name, setName] = useState("");
  const [fields, setFields] = useState<Record<string, string>>({});
  const [about, setAbout] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    endpoints
      .getTenantProfile()
      .then((c) => {
        setConfig(c);
        setName(c.name);
        const p = c.profile ?? {};
        setFields(Object.fromEntries(PROFILE_FIELDS.map((f) => [f, String(p[f] ?? "")])));
        setAbout(String(p.about ?? ""));
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  function setField(f: ProfileField, v: string) {
    setFields((s) => ({ ...s, [f]: v }));
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await endpoints.updateTenantProfile({ name, profile: { ...fields, about } });
      patchTenant({ name, profile: { ...fields, about } }); // reflect in the shell live
      toast.success(t("profile.saved"));
      setSaved(true);
      window.setTimeout(() => setSaved(false), 1600);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (error && !config) return <Card>{error}</Card>;
  if (!config)
    return (
      <div className="center-pad">
        <Spinner />
      </div>
    );

  return (
    <Card>
      <form onSubmit={save} className="profile-form">
        <Field label={t("company.name")}>
          <Input value={name} onChange={(e) => setName(e.target.value)} disabled={!canManage} />
        </Field>
        <div className="grid2-form">
          {PROFILE_FIELDS.map((f) => (
            <Field key={f} label={t(`company.${f}`)}>
              <Input
                value={fields[f] ?? ""}
                onChange={(e) => setField(f, e.target.value)}
                disabled={!canManage}
              />
            </Field>
          ))}
        </div>
        <Field label={t("company.about")}>
          <Input value={about} onChange={(e) => setAbout(e.target.value)} disabled={!canManage} />
        </Field>
        {error && <div className="form-err">{error}</div>}
        {canManage && (
          <div className="brand-actions">
            <Button type="submit" loading={busy}>
              {saved ? t("settings.saved") : t("common.save")}
            </Button>
          </div>
        )}
      </form>
    </Card>
  );
}
